import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import api from '../services/api';

// Platform > Page access > Bulk (03/11/2026: "add a bulk permission thing
// where I can tick"). People down the side, pages across the top: tick and
// untick as many boxes as needed, then Save once. Changed boxes are ringed
// until saved; Discard puts them back. Each change goes through the same
// setLevel as the one-page view, so the history records every one.
// A tick grants the page's top level (every page today is 'edit').

const errorText = (err) => err?.response?.data?.error || err?.message || 'Something went wrong.';
const keyOf = (oid, pageId) => `${oid}|${pageId}`;

function PageAccessBulk({ pages, directory }) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [onlyHolders, setOnlyHolders] = useState(true);
  // key -> true (grant) / false (remove), only where it differs from the server.
  const [pending, setPending] = useState(() => new Map());
  const [result, setResult] = useState(null);

  const grantsQuery = useQuery({
    queryKey: ['pagePermissions', 'grants'],
    queryFn: async () => (await api.get('/api/page-permissions/grants')).data.grants,
    staleTime: 0,
    retry: false,
  });

  const held = useMemo(() => {
    const set = new Set();
    for (const g of grantsQuery.data ?? []) set.add(keyOf(g.userOid.toLowerCase(), g.pageId));
    return set;
  }, [grantsQuery.data]);

  // Everyone in the directory, plus anyone holding a page who isn't in it.
  const people = useMemo(() => {
    const byOid = new Map();
    for (const p of directory.data?.people ?? []) {
      if (!p.entra_user_id || !p.display_name) continue;
      const key = p.entra_user_id.toLowerCase();
      byOid.set(key, { userOid: key, displayName: p.display_name, detail: p.job_title || p.department || null });
    }
    for (const g of grantsQuery.data ?? []) {
      const key = g.userOid.toLowerCase();
      if (!byOid.has(key)) byOid.set(key, { userOid: key, displayName: g.displayName || key, detail: null });
    }
    return [...byOid.values()].sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [directory.data, grantsQuery.data]);

  const holdsAny = (oid) => pages.some((p) => held.has(keyOf(oid, p.id)));
  const touched = (oid) => pages.some((p) => pending.has(keyOf(oid, p.id)));

  const q = search.trim().toLowerCase();
  const shown = people.filter((p) => {
    // While searching, everyone matching shows, so someone new can be ticked.
    if (q) return p.displayName.toLowerCase().includes(q) || (p.detail || '').toLowerCase().includes(q);
    return !onlyHolders || holdsAny(p.userOid) || touched(p.userOid);
  });

  const isTicked = (oid, pageId) => {
    const k = keyOf(oid, pageId);
    return pending.has(k) ? pending.get(k) : held.has(k);
  };

  const setTick = (map, oid, pageId, value) => {
    const k = keyOf(oid, pageId);
    if (value === held.has(k)) map.delete(k);
    else map.set(k, value);
  };

  const toggle = (oid, pageId) => {
    setResult(null);
    setPending((prev) => {
      const next = new Map(prev);
      setTick(next, oid, pageId, !isTicked(oid, pageId));
      return next;
    });
  };

  // A column for everyone shown, or a row across every page.
  const setColumn = (pageId, value) => {
    setResult(null);
    setPending((prev) => {
      const next = new Map(prev);
      for (const p of shown) setTick(next, p.userOid, pageId, value);
      return next;
    });
  };
  const setRow = (oid, value) => {
    setResult(null);
    setPending((prev) => {
      const next = new Map(prev);
      for (const page of pages) setTick(next, oid, page.id, value);
      return next;
    });
  };

  const nameByOid = useMemo(() => new Map(people.map((p) => [p.userOid, p.displayName])), [people]);
  const save = useMutation({
    mutationFn: async () => {
      const changes = [...pending.entries()].map(([k, value]) => {
        const [userOid, pageId] = k.split('|');
        const page = pages.find((p) => p.id === pageId);
        return {
          pageId,
          userOid,
          displayName: nameByOid.get(userOid),
          level: value ? (page?.levels?.[page.levels.length - 1] || 'edit') : 'none',
        };
      });
      return (await api.put('/api/page-permissions/bulk', { changes })).data;
    },
    onSuccess: (data) => {
      setResult(data);
      // Keep only the ones that failed, so they can be retried.
      const failedKeys = new Set((data.failed ?? []).map((f) => keyOf(f.userOid, f.pageId)));
      setPending((prev) => new Map([...prev].filter(([k]) => failedKeys.has(k))));
    },
    onError: (err) => setResult({ error: errorText(err) }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['pagePermissions'] });
    },
  });

  const loading = grantsQuery.isLoading || directory.isLoading;
  const colAllTicked = (pageId) => shown.length > 0 && shown.every((p) => isTicked(p.userOid, pageId));

  return (
    <div className="mb-6 rounded-lg border border-gray-200 bg-white">
      <div className="flex flex-col gap-3 border-b border-gray-200 p-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative sm:w-80">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Find anyone by name or role…"
              className="w-full rounded-md border border-gray-300 py-2 pl-8 pr-2.5 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={onlyHolders}
              onChange={(e) => setOnlyHolders(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-brand focus:ring-brand"
            />
            Only people with access to a page
          </label>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">
            {pending.size === 0 ? 'No unsaved changes' : `${pending.size} unsaved ${pending.size === 1 ? 'change' : 'changes'}`}
          </span>
          <button
            type="button"
            disabled={pending.size === 0 || save.isPending}
            onClick={() => { setPending(new Map()); setResult(null); }}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Discard
          </button>
          <button
            type="button"
            disabled={pending.size === 0 || save.isPending}
            onClick={() => save.mutate()}
            className="rounded-md bg-brand px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-dark disabled:opacity-50"
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      {result?.error && <p className="border-b border-gray-200 bg-red-50 px-4 py-2 text-sm text-red-700">Couldn’t save: {result.error}</p>}
      {result && !result.error && (
        <div className={`border-b border-gray-200 px-4 py-2 text-sm ${result.failed?.length ? 'bg-amber-50 text-amber-800' : 'bg-green-50 text-green-800'}`}>
          Saved {result.changed} {result.changed === 1 ? 'change' : 'changes'}.
          {result.failed?.length > 0 && (
            <> {result.failed.length} couldn’t be saved and are still ringed: {result.failed.slice(0, 3).map((f) => `${f.displayName || f.userOid} (${f.error})`).join('; ')}{result.failed.length > 3 ? '…' : ''}</>
          )}
        </div>
      )}
      {grantsQuery.isError && <p className="p-4 text-sm text-red-700">Couldn’t load who has access: {errorText(grantsQuery.error)}</p>}
      {directory.isError && <p className="border-b border-gray-200 p-4 text-sm text-amber-700">The staff directory couldn’t be loaded, so only people who already have access are shown.</p>}
      {loading && <p className="p-4 text-sm text-gray-400">Loading…</p>}

      {!loading && !grantsQuery.isError && (
        <div className="max-h-[36rem] overflow-auto">
          <table className="min-w-full border-separate border-spacing-0 text-sm">
            <thead>
              <tr>
                <th className="sticky left-0 top-0 z-20 border-b border-gray-200 bg-gray-50 px-4 py-2 text-left font-semibold text-gray-700">
                  Person
                </th>
                {pages.map((page) => (
                  <th key={page.id} title={page.description} className="sticky top-0 z-10 w-24 min-w-[6rem] border-b border-gray-200 bg-gray-50 px-2 py-2 text-center align-bottom">
                    <span className="block text-xs font-semibold leading-tight text-gray-700">{page.label}</span>
                    <label className="mt-1 inline-flex items-center gap-1 text-[11px] font-normal text-gray-500" title="Tick or untick everyone shown">
                      <input
                        type="checkbox"
                        checked={colAllTicked(page.id)}
                        disabled={shown.length === 0}
                        onChange={(e) => setColumn(page.id, e.target.checked)}
                        className="h-3.5 w-3.5 rounded border-gray-300 text-brand focus:ring-brand"
                      />
                      all
                    </label>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shown.map((person) => {
                const rowAll = pages.length > 0 && pages.every((p) => isTicked(person.userOid, p.id));
                return (
                  <tr key={person.userOid} className="hover:bg-gray-50">
                    <td className="sticky left-0 z-10 border-b border-gray-100 bg-white px-4 py-2">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={rowAll}
                          onChange={(e) => setRow(person.userOid, e.target.checked)}
                          title="Tick or untick every page for this person"
                          className="h-3.5 w-3.5 rounded border-gray-300 text-brand focus:ring-brand"
                        />
                        <span className="min-w-0">
                          <span className="block truncate font-medium text-gray-900">{person.displayName}</span>
                          {person.detail && <span className="block truncate text-xs text-gray-500">{person.detail}</span>}
                        </span>
                      </div>
                    </td>
                    {pages.map((page) => {
                      const k = keyOf(person.userOid, page.id);
                      const changed = pending.has(k);
                      return (
                        <td key={page.id} className="border-b border-gray-100 px-2 py-2 text-center">
                          <input
                            type="checkbox"
                            aria-label={`${person.displayName}: ${page.label}`}
                            checked={isTicked(person.userOid, page.id)}
                            disabled={save.isPending}
                            onChange={() => toggle(person.userOid, page.id)}
                            className={`h-5 w-5 rounded border-gray-300 text-brand focus:ring-brand ${changed ? 'ring-2 ring-amber-400 ring-offset-1' : ''}`}
                          />
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              {shown.length === 0 && (
                <tr>
                  <td colSpan={pages.length + 1} className="px-4 py-6 text-center text-sm text-gray-500">
                    {q ? 'No names match.' : 'Nobody has a page yet. Search for someone to tick them.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default PageAccessBulk;
